import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";
import { Choice, Label, cad, downloadCsv, errMsg } from "@/components/sponsored/format";

const COLORS = ["#004C3F", "#2BB673", "#F2A541", "#5B7FDB", "#C2477A", "#94A3B8"];

function Ranking({ rows }) {
  const best = Math.max(...rows.map((r) => Number(r.return_rate_pct || 0)), 1);
  return (
    <div className="overflow-x-auto" data-testid="sc-insights-table">
      <table className="w-full text-sm">
        <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
          <tr>{["Offer (first purchase)", "New eligible", "Came back", "Return rate", "Repeat revenue", "Bonus you funded", "Avg. days back"].map((h) => <th key={h} className="pb-2 pr-3 align-bottom">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key} className="border-t border-slate-100" data-testid={`sc-insights-row-${r.key}`}>
              <td className="py-2 pr-3 font-semibold text-slate-900">{i === 0 && r.came_back > 0 && <span className="pf-chip pf-chip-mint mr-2" data-testid="sc-insights-top">Top</span>}{r.name}</td>
              <td className="py-2 pr-3 tabular-nums">{r.eligible}</td>
              <td className="py-2 pr-3 tabular-nums font-semibold">{r.came_back}</td>
              <td className="py-2 pr-3 min-w-[140px]">
                {r.return_rate_pct == null ? <span className="text-xs text-slate-400">—</span> : (
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#2BB673]" style={{ width: `${(Number(r.return_rate_pct) / best) * 100}%` }} /></div>
                    <span className="tabular-nums text-xs w-12 text-right">{r.return_rate_pct}%</span>
                  </div>
                )}
              </td>
              <td className="py-2 pr-3 tabular-nums">{cad(r.repeat_revenue_cad)}</td>
              <td className="py-2 pr-3 tabular-nums text-[#004C3F]">{cad(r.bonus_funded_cad)}</td>
              <td className="py-2 pr-3 tabular-nums">{r.avg_days_to_return == null ? "—" : Number(r.avg_days_to_return).toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Timeline({ data }) {
  return (
    <div className="h-64" data-testid="sc-insights-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.timeline} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} tickFormatter={(v) => `CA$${v}`} width={48} />
          <Tooltip formatter={(v) => cad(v)} cursor={{ fill: "rgba(0,76,63,0.05)" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: "#334155" }}>{v}</span>} />
          {data.series.map((s, i) => <Bar key={s.key} dataKey={s.key} name={s.name} stackId="rev" fill={COLORS[i % COLORS.length]} radius={i === data.series.length - 1 ? [6, 6, 0, 0] : 0} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function InsightsCard() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/merchant/sponsored-cashback/insights", { params: { months } }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [months]);
  const empty = data && !data.error && data.totals.eligible === 0 && data.totals.came_back === 0;
  const [busy, setBusy] = useState(false);
  const exportCsv = () => {
    setBusy(true);
    downloadCsv("/merchant/sponsored-cashback/insights.csv", { months }, `sponsored-cashback-insights-${months}mo-${new Date().toISOString().slice(0, 10)}.csv`)
      .then(() => toast.success("Spreadsheet downloaded"))
      .catch((e) => toast.error(errMsg(e, "Couldn't download the spreadsheet")))
      .finally(() => setBusy(false));
  };
  return (
    <section className="pf-card p-6 space-y-5" data-testid="sc-insights-card">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-xl font-medium text-slate-900">Repeat-shopper insights</h3>
          <p className="text-sm text-slate-600 mt-1">Which offers bring shoppers back. Each return is credited to the offer behind the shopper's first purchase.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label>Period</Label><Choice options={[3, 6, 12]} value={months} onChange={setMonths} suffix=" mo" testid="sc-insights-months" /></div>
          <button type="button" onClick={exportCsv} disabled={busy || !data || data.error || empty} data-testid="sc-insights-csv-btn"
            className="pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 transition-opacity duration-200 disabled:opacity-50">
            <Download className="w-4 h-4" /> {busy ? "Preparing…" : "Download spreadsheet"}
          </button>
        </div>
      </div>
      {!data ? <p className="text-sm text-slate-500">Loading…</p> : data.error ? <p className="text-sm text-rose-600" data-testid="sc-insights-error">Couldn't load insights.</p> : empty ? (
        <p className="text-sm text-slate-400 italic" data-testid="sc-insights-empty">No eligible or returning shoppers in this period yet.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-6 text-sm" data-testid="sc-insights-totals">
            <span><b className="tabular-nums">{data.totals.eligible}</b> new eligible shoppers</span>
            <span><b className="tabular-nums">{data.totals.came_back}</b> came back</span>
            <span><b className="tabular-nums">{cad(data.totals.repeat_revenue_cad)}</b> repeat revenue in this period</span>
          </div>
          <Ranking rows={data.campaigns} />
          <div><Label>Repeat revenue per offer, by month</Label><div className="mt-3"><Timeline data={data} /></div></div>
        </>
      )}
    </section>
  );
}
